import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import shared from "@/seo/shared";
import { useSeo } from "@/seo/useSeo";
import { SERVICES } from "@/components/site/data";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";
import { SubHero } from "@/components/site/SubHero";
import { Process } from "@/components/site/Process";
import { Contact } from "@/components/site/Contact";
import { Reveal } from "@/components/site/Reveal";

const Block = ({ h2, children, tone = "light" }) => (
  <Reveal>
    <h2 className={`font-display text-4xl font-bold uppercase leading-[0.95] sm:text-5xl ${tone === "light" ? "text-ink" : "text-white"}`}>{h2}</h2>
    <div className="mt-6 space-y-4">{children}</div>
  </Reveal>
);

const List = ({ items }) => (
  <ul className="grid gap-3 sm:grid-cols-2">
    {items.map((b) => <li key={b} className="flex gap-3 text-[0.95rem] text-slate-800"><Check size={18} className="mt-0.5 shrink-0 text-terra-ink" />{b}</li>)}
  </ul>
);

export const RelatedLinks = ({ slugs, title = "Verwandte Leistungen" }) => (
  <div className="mt-16 border-t border-ink/15 pt-10">
    <h2 className="font-display text-3xl font-bold uppercase text-ink">{title}</h2>
    <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {slugs.map((slug) => {
        const s = shared.services.find((x) => x.slug === slug);
        return <Link key={slug} to={`/${slug}`} className="btn-dark justify-between !px-5" data-testid={`related-${slug}`}>{s.name} <ArrowRight size={16} className="btn-arrow" /></Link>;
      })}
      <Link to="/" className="btn-dark justify-between !px-5" data-testid="related-home">Zur Startseite <ArrowRight size={16} className="btn-arrow" /></Link>
    </div>
  </div>
);

export default function ServicePage({ s, consent, onCookies }) {
  const icon = SERVICES.find((x) => x.id === s.sid);
  const P = ({ children }) => <p className="text-base leading-relaxed text-slate-700 sm:text-lg">{children}</p>;
  useSeo({ path: `/${s.slug}`, title: s.title, description: s.description, ld: shared.serviceLd(s) });

  return (
    <>
      <Nav />
      <main data-testid={`service-page-${s.slug}`}>
        <SubHero h1={s.h1} lead={s.lead} img={s.img} alt={s.alt} crumbs={[{ name: "Startseite", path: "/" }, { name: s.name, path: `/${s.slug}` }]} />
        <section className="bg-paper py-20 text-ink sm:py-28">
          <div className="mx-auto grid max-w-[1440px] gap-16 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
            <div className="space-y-16 lg:col-span-8">
              <Block h2={s.what.h2}>{s.what.paras.map((p) => <P key={p.slice(0, 20)}>{p}</P>)}<List items={s.what.bullets} /></Block>
              <Block h2={s.cost.h2}>{s.cost.paras.map((p) => <P key={p.slice(0, 20)}>{p}</P>)}<List items={s.cost.factors} /></Block>
              <Block h2={s.area.h2}><P>{s.area.text}</P><Link to="/einsatzgebiet" className="btn-dark mt-2 w-fit" data-testid="service-area-link">Ganzes Einsatzgebiet ansehen <ArrowRight size={16} className="btn-arrow" /></Link></Block>
              <Block h2="Häufige Fragen">
                <Accordion type="single" collapsible className="border-t border-ink/15" data-testid="service-faq">
                  {s.faqs.map((f, i) => (
                    <AccordionItem key={f.q} value={`f-${i}`} className="border-b border-ink/15">
                      <AccordionTrigger className="py-6 text-left font-display text-xl font-bold uppercase leading-tight text-ink hover:no-underline [&>svg]:text-terra-ink">{f.q}</AccordionTrigger>
                      <AccordionContent className="pb-6 text-base leading-relaxed text-slate-700">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Block>
            </div>
            <aside className="lg:col-span-4">
              <div className="sticky top-28 bg-ink p-8 text-white">
                {icon && <icon.icon size={36} className="text-terra" />}
                <p className="mt-5 font-display text-3xl font-bold uppercase leading-none">{icon?.benefit}</p>
                <p className="mt-4 text-sm leading-relaxed text-slate-300">Kostenlose Besichtigung innerhalb von 48 Stunden, verbindliches Festpreisangebot, Meisterbetrieb seit 2008.</p>
              </div>
            </aside>
          </div>
          <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12"><RelatedLinks slugs={s.related} /></div>
        </section>
        <Process />
        <Contact consent={consent} />
      </main>
      <Footer onCookies={onCookies} />
    </>
  );
}
